/**
 * Cadre du PDA stock (« VisionStock ») : thème sombre et accent or comme l'accueil du terminal,
 * en-tête compact (nom, interrupteur rafale, point réseau), onglets fixes en bas et menu « Plus ».
 * Le scan est capté sur tous les écrans. Ni caisse ni bannières de caisse.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import visionPosIcon from '@/assets/posvision-icon.png';
import ImpersonateBanner from '@/components/ImpersonateBanner';
import { NetworkDot } from '@/features/pda/NetworkDot';
import { PdaBottomNav } from '@/features/pda/PdaBottomNav';
import { PdaMoreSheet, type PdaTheme } from '@/features/pda/PdaMoreSheet';
import { usePdaLaunchUrl } from '@/features/pda/usePdaLaunchUrl';
import { usePdaScanCapture } from '@/features/pda/usePdaScanCapture';
import { BurstToggle, useBurst } from '@/features/stock-mode';
import { dispatchPdaScan } from '@/lib/pdaScanEvent';
import { useLocalStorageState } from '@/hooks/useLocalStorageState';
import { acceptScan } from '@/lib/scanSources';

const THEME_CLASSES = ['dark', 'gold', 'royal', 'nardo'];

/**
 * Sombre et or par défaut, clair au choix (« Plus »), mémorisé sur l'appareil.
 * Le thème de l'appareil revient en quittant le profil.
 */
function usePdaTheme(theme: PdaTheme) {
  useEffect(() => {
    const root = document.documentElement;
    const previous = THEME_CLASSES.filter((name) => root.classList.contains(name));
    root.classList.add('vp-pda');
    return () => {
      root.classList.remove('vp-pda', ...THEME_CLASSES);
      root.classList.add(...previous);
    };
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...THEME_CLASSES);
    if (theme === 'dark') root.classList.add('dark');
  }, [theme]);
}

export function PdaLayout({ children }: { children: ReactNode }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const burst = useBurst();
  useEffect(() => setMoreOpen(false), [pathname, search]);
  const [storedTheme, setTheme] = useLocalStorageState<PdaTheme>('vp_pda_theme', 'dark');
  const theme: PdaTheme = storedTheme === 'light' ? 'light' : 'dark';
  usePdaTheme(theme);
  usePdaLaunchUrl();
  usePdaScanCapture((code) => {
    setMoreOpen(false);
    // Le Mode Stock décide selon son onglet (réception, inventaire, rafale, question).
    if (pathname === '/mode-stock') return dispatchPdaScan(code);
    if (!acceptScan(code, 'keyboard')) return undefined;
    // Code ignoré, QR code : bandeau sur place, on ne quitte pas l'écran.
    if (!burst.screen(code)) return undefined;
    // Rafale : +1 sur place, sans quitter l'écran.
    if (burst.enabled) return void burst.scan(code);
    return navigate(`/mode-stock?vue=maj&src=scan&code=${encodeURIComponent(code)}`);
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background" data-testid="pda-layout">
      <ImpersonateBanner />
      <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-black pl-3 pr-1">
        <img
          src={visionPosIcon}
          alt="VisionPOS"
          className="h-8 w-8 shrink-0"
          data-testid="pda-logo"
        />
        <span className="min-w-0 flex-1 truncate text-lg font-bold tracking-tight text-white">
          Vision<span className="text-[#FECC00]">Stock</span>
        </span>
        <BurstToggle />
        <NetworkDot />
      </header>
      <main className="min-w-0 flex-1 pb-24">{children}</main>
      <PdaBottomNav moreOpen={moreOpen} onMore={() => setMoreOpen(true)} />
      <PdaMoreSheet open={moreOpen} onOpenChange={setMoreOpen} theme={theme} onTheme={setTheme} />
    </div>
  );
}
